import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Percent } from "lucide-react";

const ROLE_STYLE = {
    admin: "bg-navy-700 text-white",
    broker: "bg-gold-500 text-navy-900",
    affiliate: "bg-emerald-600 text-white",
    member: "bg-navy-50 text-navy-700 border-navy-100 border",
};
const ALL_ROLES = ["member", "broker", "affiliate", "admin"];

export default function Users() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [tiers, setTiers] = useState([]);
    const [editingUser, setEditingUser] = useState(null); // full user row
    const [selectedTier, setSelectedTier] = useState("__auto__");
    const [savingOverride, setSavingOverride] = useState(false);
    // Roles editor
    const [rolesUser, setRolesUser] = useState(null);
    const [rolesDraft, setRolesDraft] = useState([]);
    const [savingRoles, setSavingRoles] = useState(false);

    const load = async () => {
        const [uRes, dRes] = await Promise.all([
            api.get("/admin/users"),
            api.get("/admin/stripe-discounts"),
        ]);
        setUsers(uRes.data);
        setTiers(dRes.data);
    };
    useEffect(() => {
        load();
    }, []);

    const tierMap = Object.fromEntries(tiers.map((t) => [t.id, t]));

    const filtered = users.filter((u) =>
        `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase())
    );

    const openOverride = (u) => {
        setEditingUser(u);
        setSelectedTier(u.admin_discount_override_id || "__auto__");
    };

    const saveOverride = async () => {
        if (!editingUser) return;
        setSavingOverride(true);
        try {
            await api.post(`/admin/users/${editingUser.id}/discount-override`, {
                stripe_discount_id:
                    selectedTier === "__auto__" ? null : selectedTier,
            });
            toast.success(
                selectedTier === "__auto__"
                    ? "Override cleared — back to auto-threshold"
                    : "Admin discount override applied"
            );
            setEditingUser(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingOverride(false);
        }
    };

    const openRoles = (u) => {
        setRolesUser(u);
        setRolesDraft([...(u.roles || (u.role ? [u.role] : []))]);
    };
    const toggleRole = (r) =>
        setRolesDraft((d) => (d.includes(r) ? d.filter((x) => x !== r) : [...d, r]));
    const saveRoles = async () => {
        if (!rolesUser) return;
        if (rolesDraft.length === 0) {
            toast.error("A user must hold at least one role.");
            return;
        }
        setSavingRoles(true);
        try {
            await api.patch(`/admin/users/${rolesUser.id}/roles`, {
                roles: rolesDraft,
            });
            toast.success(`Roles updated for ${rolesUser.name}`);
            setRolesUser(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingRoles(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Directory"
                title="Users"
                description="Every soul with a Perx login — members, vendors and admins."
                testid="admin-users-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="relative max-w-md">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-500"
                    />
                    <Input
                        data-testid="users-search"
                        placeholder="Search by name, email or role…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                    />
                </div>

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <Table data-testid="users-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    User
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Email
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Role
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Discount
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Membership
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Joined
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((u) => (
                                <TableRow
                                    key={u.id}
                                    data-testid={`user-row-${u.id}`}
                                    onClick={() => navigate(`/admin/users/${u.id}`)}
                                    className="cursor-pointer hover:bg-cream/60"
                                >
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-gold-500/10 text-gold-700 flex items-center justify-center font-semibold text-sm">
                                                {u.name?.[0]}
                                            </div>
                                            <span className="text-navy-700 font-medium">
                                                {u.name}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-navy-500">
                                        {u.email}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {(u.roles || [u.role]).map((r) => (
                                                <Badge
                                                    key={r}
                                                    className={`${ROLE_STYLE[r] || ROLE_STYLE.member} border-0 uppercase tracking-widest text-[10px]`}
                                                >
                                                    {r}
                                                </Badge>
                                            ))}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openRoles(u);
                                                }}
                                                data-testid={`user-roles-edit-${u.id}`}
                                                className="h-6 px-2 text-[10px] text-navy-400 hover:text-navy-700"
                                            >
                                                Edit
                                            </Button>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {(u.roles || [u.role]).includes("member") ? (
                                            <UserDiscountCell
                                                user={u}
                                                tierMap={tierMap}
                                                onEdit={(e) => {
                                                    e?.stopPropagation?.();
                                                    openOverride(u);
                                                }}
                                            />
                                        ) : (
                                            <span className="text-navy-300 text-xs">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            data-testid={`user-membership-${u.id}`}
                                            className={`border-0 uppercase tracking-widest text-[10px] ${
                                                u.membership_active
                                                    ? "bg-emerald-600 text-white"
                                                    : "bg-navy-50 text-navy-500 border border-navy-100"
                                            }`}
                                        >
                                            {u.membership_active ? "Active" : "Inactive"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-navy-500">
                                        {u.created_at
                                            ? new Date(u.created_at).toLocaleDateString()
                                            : "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog
                open={!!editingUser}
                onOpenChange={(v) => !v && setEditingUser(null)}
            >
                <DialogContent
                    data-testid="user-override-dialog"
                    className="max-w-md bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl text-navy-700">
                            Discount override
                        </DialogTitle>
                        <DialogDescription>
                            Manually apply a discount to{" "}
                            <span className="font-semibold">{editingUser?.name}</span>
                            &apos;s Stripe membership. This overrides the automatic referral
                            threshold logic.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Select value={selectedTier} onValueChange={setSelectedTier}>
                            <SelectTrigger
                                data-testid="user-override-select"
                                className="h-11 bg-cream border-navy-100"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__auto__">
                                    Auto (based on referrals — no override)
                                </SelectItem>
                                {tiers.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        {t.name} · {t.discount_pct}% off
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="text-[11px] text-navy-500">
                            Selecting Auto clears any active override and re-evaluates the
                            member&apos;s Stripe subscription against the referral-threshold
                            tiers.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEditingUser(null)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveOverride}
                            disabled={savingOverride}
                            data-testid="user-override-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream"
                        >
                            {savingOverride ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!rolesUser}
                onOpenChange={(v) => !v && setRolesUser(null)}
            >
                <DialogContent
                    data-testid="user-roles-dialog"
                    className="max-w-md bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl text-navy-700">
                            Manage roles
                        </DialogTitle>
                        <DialogDescription>
                            Grant{" "}
                            <span className="font-semibold">{rolesUser?.name}</span>{" "}
                            access to any combination of portals. They&apos;ll see a
                            switcher in their sidebar for every role they hold.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        {ALL_ROLES.map((r) => {
                            const held = rolesDraft.includes(r);
                            return (
                                <label
                                    key={r}
                                    className={`flex items-center justify-between rounded-xl border p-3 cursor-pointer transition ${
                                        held
                                            ? "border-gold-400 bg-gold-50"
                                            : "border-navy-100 bg-white hover:bg-cream"
                                    }`}
                                >
                                    <div>
                                        <div className="text-sm text-navy-700 font-semibold capitalize">
                                            {r}
                                        </div>
                                        <div className="text-[11px] text-navy-500">
                                            {r === "admin"
                                                ? "Full admin console access — grant with care."
                                                : r === "affiliate"
                                                ? "Promoter dashboard, referral tools, panda deck."
                                                : r === "broker"
                                                ? "Vendor portal — product profiles, applications, payouts."
                                                : "Member portal — deals, subscription, family."}
                                        </div>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={held}
                                        onChange={() => toggleRole(r)}
                                        data-testid={`user-role-check-${r}`}
                                        className="h-5 w-5 accent-gold-500 cursor-pointer"
                                    />
                                </label>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setRolesUser(null)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveRoles}
                            disabled={savingRoles || rolesDraft.length === 0}
                            data-testid="user-roles-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-cream"
                        >
                            {savingRoles ? "Saving…" : "Save roles"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function UserDiscountCell({ user, tierMap, onEdit }) {
    const override = user.admin_discount_override_id
        ? tierMap[user.admin_discount_override_id]
        : null;
    const auto = user.current_referral_tier_id
        ? tierMap[user.current_referral_tier_id]
        : null;
    return (
        <div className="flex items-center gap-2">
            {override ? (
                <Badge className="bg-navy-700 text-cream border-0">
                    Admin · {override.discount_pct}%
                </Badge>
            ) : auto ? (
                <Badge className="bg-gold-500 text-navy-900 border-0">
                    Auto · {auto.discount_pct}%
                </Badge>
            ) : (
                <span className="text-navy-400 text-xs">None</span>
            )}
            <Button
                variant="ghost"
                size="sm"
                onClick={(e) => onEdit(e)}
                data-testid={`user-override-edit-${user.id}`}
                className="h-7 px-2 text-xs text-navy-500 hover:text-navy-700"
            >
                <Percent size={12} className="mr-1" />
                Edit
            </Button>
        </div>
    );
}
